import { CATEGORY_LABEL } from "../map/model";
import type { Category, CategoryStyle, MapData } from "../map/model";

interface SearchEntry {
  locationId: string;
  level: string;
  name: string;
  category: Category;
  categoryLabel: string;
  haystack: string;
}

export interface SearchHandlers {
  /** Bir sonuç seçilince çağrılır (locationId, seçilen ünitenin katı + kategorisi). */
  onSelect: (locationId: string, level: string, category: Category) => void;
}

const MAX_RESULTS = 8;

/**
 * İsim, kiracı, kategori, ref kodu VEYA kata göre lokasyon/ünite araması.
 * Yazınca eşleşen üniteleri liste olarak gösterir; seçilince onSelect
 * (locationId, level) çağrılır. Yalnızca haritada işaretli (svgParcelId'i
 * olan) lokasyonlar aranır.
 */
export function createSearch(
  host: HTMLElement,
  data: MapData,
  stylesByCategory: Map<Category, CategoryStyle>,
  handlers: SearchHandlers,
) {
  const entries: SearchEntry[] = [];
  for (const loc of data.locations) {
    if (!loc.svgParcelId) continue;
    for (const u of loc.units) {
      const name = u.tenantName ?? loc.name;
      entries.push({
        locationId: loc.id,
        level: u.floorLevel,
        name,
        category: u.category,
        categoryLabel: CATEGORY_LABEL[u.category],
        haystack: `${loc.name} ${name} ${CATEGORY_LABEL[u.category]} ${loc.mapRefCode ?? ""} ${u.floorLevel}`.toLowerCase(),
      });
    }
  }

  const box = document.createElement("div");
  box.className = "search-box";

  const icon = document.createElement("span");
  icon.className = "search-icon";
  icon.textContent = "🔍";

  const input = document.createElement("input");
  input.type = "search";
  input.className = "search-input";
  input.placeholder = "Search a location, tenant or category…";
  input.setAttribute("aria-label", "Search locations");
  input.autocomplete = "off";

  const results = document.createElement("div");
  results.className = "search-results";
  results.hidden = true;

  box.append(icon, input, results);
  host.appendChild(box);

  let current: SearchEntry[] = [];

  function render(raw: string) {
    const query = raw.trim().toLowerCase();
    if (!query) {
      results.hidden = true;
      results.innerHTML = "";
      current = [];
      return;
    }
    current = entries.filter((e) => e.haystack.includes(query)).slice(0, MAX_RESULTS);
    if (!current.length) {
      results.innerHTML = `<div class="search-empty">No matches</div>`;
      results.hidden = false;
      return;
    }
    results.innerHTML = current
      .map(
        (e, i) =>
          `<button type="button" class="search-row" data-i="${i}">` +
          `<span class="search-dot" style="background:${esc(stylesByCategory.get(e.category)?.fill ?? "#94a3b8")}"></span>` +
          `<span class="search-name">${esc(e.name)}</span>` +
          `<span class="search-floor">${esc(e.level)}</span>` +
          `<span class="search-cat">${esc(e.categoryLabel)}</span>` +
          `</button>`,
      )
      .join("");
    results.hidden = false;
  }

  function choose(entry: SearchEntry) {
    input.value = entry.name;
    results.hidden = true;
    input.blur();
    handlers.onSelect(entry.locationId, entry.level, entry.category);
  }

  input.addEventListener("input", () => render(input.value));
  input.addEventListener("focus", () => { if (input.value.trim()) render(input.value); });
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && current.length) { e.preventDefault(); choose(current[0]); }
    else if (e.key === "Escape") { input.value = ""; render(""); input.blur(); }
  });
  results.addEventListener("click", (e) => {
    const btn = (e.target as Element).closest<HTMLButtonElement>(".search-row");
    if (!btn) return;
    const i = Number(btn.dataset.i);
    if (current[i]) choose(current[i]);
  });
  document.addEventListener("click", (e) => {
    if (!box.contains(e.target as Node)) results.hidden = true;
  });
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}
